import { useState } from "react";
import { Loader2, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { formatCurrencyEUR } from "@/lib/utils";
import { formatApiError } from "@/lib/format-api-error";
import type { FundAllocationDetail, MdfFundStatus } from "./types";
import { useRemoveAllocation } from "./use-remove-allocation";
import { AddAllocationDialog } from "./add-allocation-dialog";
import { EditAllocationDialog } from "./edit-allocation-dialog";

// Sub-bucket 15 — Allocations tab. Each row shows partner / cap / used /
// balance / notes + a row dropdown (Edit / Remove). Add allocation triggers
// at the top right. All writes route through Phase 6 mutation hooks; the
// fund-detail query invalidates after each, so the rows re-render.

export function FundAllocationsTab({
  fundId,
  allocations,
  fundStatus,
}: {
  fundId: string;
  allocations: FundAllocationDetail[];
  fundStatus: MdfFundStatus;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<FundAllocationDetail | null>(null);
  const [confirmRemove, setConfirmRemove] =
    useState<FundAllocationDetail | null>(null);
  const remove = useRemoveAllocation();
  const fundClosed = fundStatus === "closed";

  function handleRemove() {
    if (!confirmRemove) return;
    remove.mutate(
      { id: confirmRemove.id, fundId },
      {
        onSuccess: () => {
          toast.success("Allocation removed");
          setConfirmRemove(null);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to remove allocation"));
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {allocations.length === 0
            ? "No allocations yet. Add a partner to allocate budget."
            : `${allocations.length} partner allocation${allocations.length === 1 ? "" : "s"} in this fund.`}
        </p>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
          disabled={fundClosed}
          title={fundClosed ? "Reopen the fund to add allocations" : undefined}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add allocation
        </Button>
      </div>

      {allocations.length === 0 ? (
        <EmptyState
          title="No allocations"
          description="Allocate a portion of this fund to a partner so they can submit MDF requests."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">Partner</th>
                <th className="px-4 py-2.5 text-right">Cap</th>
                <th className="px-4 py-2.5 text-right">Used</th>
                <th className="px-4 py-2.5 text-right">Balance</th>
                <th className="px-4 py-2.5">Notes</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70">
              {allocations.map((row) => {
                const isRemovingRow =
                  remove.isPending &&
                  confirmRemove?.id === row.id;
                return (
                  <tr key={row.id} className="group hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">
                      {row.partner_name ?? "Unknown partner"}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatCurrencyEUR(row.cap_amount_eur)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatCurrencyEUR(row.used_amount_eur)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatCurrencyEUR(row.balance_amount_eur)}
                    </td>
                    <td className="max-w-[280px] truncate px-4 py-3 text-xs text-muted-foreground">
                      {row.notes ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isRemovingRow}
                            aria-label={`Actions for ${row.partner_name ?? "allocation"}`}
                          >
                            {isRemovingRow ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditing(row)}>
                            Edit cap & notes
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setConfirmRemove(row)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AddAllocationDialog
        fundId={fundId}
        existingPartnerIds={allocations.map((a) => a.partner_id)}
        open={addOpen}
        onOpenChange={setAddOpen}
      />

      {editing && (
        <EditAllocationDialog
          fundId={fundId}
          allocation={editing}
          open={!!editing}
          onOpenChange={(o) => {
            if (!o) setEditing(null);
          }}
        />
      )}

      <AlertDialog
        open={!!confirmRemove}
        onOpenChange={(o) => {
          if (!o && !remove.isPending) setConfirmRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove allocation?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRemove?.partner_name ?? "This partner"}&apos;s allocation
              will be removed from this fund. Requests already approved or
              marked spent stay intact for audit; this operation only succeeds
              when no committed spend exists.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={remove.isPending}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
